import React, { useState, useEffect, useMemo, useRef } from 'react';
import { io } from 'socket.io-client';
import { vorschauPunkte } from '../lib/bruchtestVorschau';

// ─── Konstanten ──────────────────────────────────────────────────────────────
const formatTime = (s) => {
  const sec = Math.max(0, Math.floor(s));
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};

// Welche Bedienung gehoert zu welcher Disziplin?
//
// Massgeblich ist die Angabe aus den Daten (divlisttdaalle.Division_Type),
// die der Server jetzt mitliefert. Frueher wurde sie aus dem Divisionscode
// GERATEN -- und das ging bei Formen regelmaessig schief: DTUBF-1, JUBF-231
// und SD-9 enthalten weder "FORM" noch "KATA" und galten als Kumite. Der
// Kampfrichter bekam Punkteknoepfe statt der Notenskala, und nichts erreichte
// formen_wertungen.
const TYP_ZU_MODUS = {
  'formen': 'kata',
  'bruchtest': 'bruchtest',
  'kickboxen': 'kickboxen',
  'k1': 'kickboxen',
  'continous fighting': 'kickboxen',
  'pointfighting': 'kickboxen',
  'fullcontact': 'kickboxen',
  'kumite': 'kumite',
  'grappling': 'kumite',
  'rumble': 'kumite',
  // Selbstverteidigung fehlt hier mit Absicht: dafuer gibt es in der App
  // keine Bedienung. In formen_wertungen ist kampfrichter_nummer dort die
  // UEBUNGSnummer -- die Kata-Maske wuerde Kampfrichter als Uebungen
  // eintragen. Solange das nicht gebaut ist, bleibt es beim Vorgabemodus,
  // und der Hauptrichter entscheidet bewusst.
};

const getSessionTyp = (code, divisionType) => {
  const ausDenDaten = TYP_ZU_MODUS[String(divisionType || '').trim().toLowerCase()];
  if (ausDenDaten) return ausDenDaten;

  // Kein oder unbekannter Typ: die alte Schaetzung als Notnagel.
  if (!code) return 'kumite';
  const c = code.toUpperCase();
  if (c.includes('BRUCH') || c.includes('BREAK')) return 'bruchtest';
  if (c.startsWith('BBF') || c.includes('FORM') || c.includes('KATA') || c.includes('WUSHU')) return 'kata';
  if (c.includes('KICK') || c.includes('KB') || c.includes('LIGHT') || c.includes('FULL') || c.includes('K-1')
    || c.includes('CONT') || c.includes('POINT') || c.startsWith('CS') || c.startsWith('CF')) return 'kickboxen';
  return 'kumite';
};

// Techniken, Materialien und Sprung-Faktoren kommen vom Server
// (GET /api/kampfrichter/bruchtest-katalog -> utils/bruchtestKatalog.js).
//
// Hier standen eigene Listen mit eigenen Multiplikatoren: Hammerfaust 1,0
// statt 1,1, Handruecken 1,1 statt 1,2, Ellbogen 1,2 statt 1,3, Drehkick 1,4
// statt 1,5 -- und das Material hiess 'schlaeger' statt 'baseballschlaeger'.
// Derselbe Bruch gab am Tablet eine andere Zahl als am Schreibtisch.
// Gerechnet wird ohnehin am Server; lib/bruchtestVorschau.js zeigt nur an,
// was dort herauskommen wird.

// ─── Design-Tokens ───────────────────────────────────────────────────────────
const GOLD   = '#d9aa43';
const GREEN  = '#34d399';
const ROT_C  = '#ef4444';
const BLAU_C = '#3b82f6';
const ORANGE = '#fb923c';
const ROT_BG = 'rgba(239,68,68,0.15)';
const BLAU_BG= 'rgba(59,130,246,0.15)';
const BORDER = 'rgba(255,255,255,0.1)';
const SURFACE= 'rgba(255,255,255,0.05)';

const bs = (bg, color = '#fff', border) => ({
  background: bg, color,
  border: `1px solid ${border || bg}`,
  borderRadius: 12, padding: '0.75rem 1rem',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontWeight: 700, fontSize: '0.875rem',
  cursor: 'pointer', userSelect: 'none',
  WebkitUserSelect: 'none', touchAction: 'manipulation',
  display: 'flex', alignItems: 'center',
  justifyContent: 'center', minHeight: 44,
  lineHeight: 1.2, textAlign: 'center', outline: 'none',
});

const DEFAULT_MAT = {
  status: 'waiting', session_typ: 'kumite',
  rote_punkte: 0, blaue_punkte: 0,
  rote_warnings: 0, blaue_warnings: 0,
  timer_seconds: 180, timer_remaining: 180,
  timer_running: false, kata_notes: {},
  bruchtest_rot_scores: [], bruchtest_blau_scores: [],
  bruchtest_active_side: 'rot',
  fight_info: null, winner: null, connected_referees: [],
};

// ════════════════════════════════════════════════════════════════════════════
//  ROOT
// ════════════════════════════════════════════════════════════════════════════
export default function JudgeApp() {
  const matNr = parseInt(window.location.pathname.match(/\/judge\/(\d+)/)?.[1]) || 1;
  const [user, setUser] = useState(null);
  const [role, setRole] = useState('seite');
  const [lf, setLf] = useState({ username: '', password: '' });
  const [loginErr, setLoginErr] = useState('');
  const [loading, setLoading] = useState(false);
  // Aufgabe 132: schon im gemeinsamen Anmeldefenster (/anmelden) angemeldet —
  // dann fehlt nur noch die Rolle (Center/Judge). Gilt nur für diesen Tab.
  const [vorab] = useState(() => {
    try { const v = JSON.parse(sessionStorage.getItem('kr_anmeldung') || 'null'); return v?.token ? v : null; }
    catch { return null; }
  });

  async function doLogin(e) {
    e.preventDefault();
    if (vorab) { setUser({ ...vorab.kampfrichter, token: vorab.token, role }); return; }
    setLoading(true); setLoginErr('');
    try {
      const r = await fetch('/api/kampfrichter/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: lf.username, password: lf.password }),
      });
      const d = await r.json();
      if (d.success) setUser({ ...d.kampfrichter, token: d.token, role });
      else setLoginErr(d.error || 'Login fehlgeschlagen');
    } catch { setLoginErr('Verbindungsfehler'); }
    setLoading(false);
  }

  if (!user) return (
    <div style={{ background: '#0a0a14', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif', color: '#fff', padding: '1rem' }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem' }}>⚖️</div>
          <h1 style={{ margin: '0.5rem 0 0.25rem', fontSize: '1.5rem', fontWeight: 700 }}>Kampfrichter</h1>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem' }}>Matte {matNr}</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
          {[['haupt','🟡','Center','Hauptrichter'],['seite','⚪','Judge','Seitenrichter']].map(([val,ico,lbl,sub]) => (
            <button key={val} onClick={() => setRole(val)}
              style={{ ...bs(role===val ? (val==='haupt'?'rgba(215,162,58,0.2)':'rgba(255,255,255,0.1)') : SURFACE, role===val?(val==='haupt'?GOLD:'#fff'):'rgba(255,255,255,0.4)', role===val?(val==='haupt'?GOLD:BORDER):BORDER), flexDirection:'column', gap:'0.25rem', minHeight:72, padding:'1rem' }}>
              <span style={{fontSize:'1.4rem'}}>{ico}</span>
              <span style={{fontSize:'1rem'}}>{lbl}</span>
              <span style={{fontSize:'0.7rem',fontWeight:400,opacity:0.6}}>{sub}</span>
            </button>
          ))}
        </div>
        <form onSubmit={doLogin} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 20, padding: '1.25rem' }}>
          {vorab && <div style={{ marginBottom:'0.75rem', fontSize:'0.9rem', color:'rgba(255,255,255,0.75)' }}>Angemeldet als <strong>{vorab.kampfrichter?.name}</strong> — Rolle oben wählen.</div>}
          {!vorab && [['username','Benutzername','text','none',role==='haupt'?'center':'judge1'],['password','Passwort','password','current-password','']].map(([fld,lbl,type,ac,ph]) => (
            <label key={fld} style={{ display:'block', marginBottom:'0.75rem' }}>
              <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.4)', marginBottom:'0.35rem', textTransform:'uppercase', letterSpacing:'0.05em' }}>{lbl}</div>
              <input style={{ width:'100%', padding:'0.8rem 1rem', background:'rgba(255,255,255,0.07)', border:`1px solid ${BORDER}`, borderRadius:12, color:'#fff', fontSize:'1rem', fontFamily:'inherit', boxSizing:'border-box', outline:'none' }}
                type={type} autoCapitalize={type==='text'?'none':undefined} autoComplete={ac} placeholder={ph}
                value={lf[fld]} onChange={e => setLf(p => ({...p,[fld]:e.target.value}))} required />
            </label>
          ))}
          {loginErr && <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:10, padding:'0.6rem 0.9rem', marginBottom:'0.75rem', fontSize:'0.875rem', color:'#fca5a5' }}>{loginErr}</div>}
          <button type="submit" disabled={loading} style={{ ...bs(GOLD,'#000',GOLD), width:'100%', fontSize:'1rem', opacity:loading?0.6:1 }}>
            {loading?'Einloggen…':`${vorab ? 'Weiter' : 'Einloggen'} als ${role==='haupt'?'Center':'Judge'}`}
          </button>
        </form>
      </div>
    </div>
  );

  return <MatPanel user={user} matNr={matNr} onLogout={() => setUser(null)} />;
}

// ════════════════════════════════════════════════════════════════════════════
//  MAT PANEL
// ════════════════════════════════════════════════════════════════════════════
function MatPanel({ user, matNr, onLogout }) {
  const [connected, setConnected] = useState(false);
  // Bruchtest-Katalog vom Server: Techniken mit Gruppe und Multiplikator,
  // Materialien mit Punkten je Stueck, Sprung-Faktoren je Gruppe.
  const [katalog, setKatalog] = useState({ techniken: [], materialien: [], sprungMultiplikatoren: {} });

  useEffect(() => {
    let abgebrochen = false;
    fetch('/api/kampfrichter/bruchtest-katalog', {
      headers: { Authorization: 'Bearer ' + user.token },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (abgebrochen || !d?.success) return;
        setKatalog({
          techniken: d.data || [],
          materialien: d.materialien || [],
          sprungMultiplikatoren: d.sprung_multiplikatoren || {},
        });
      })
      .catch(() => { /* ohne Katalog bleibt die Auswahl leer */ });
    return () => { abgebrochen = true; };
  }, [user.token]);
  const [matState, setMatState] = useState({ ...DEFAULT_MAT });
  const [showCat, setShowCat] = useState(false);
  const [kataNote, setKataNote] = useState(7.0);
  const [noteSubmitted, setNoteSubmitted] = useState(false);
  const [tiebreakerMode, setTiebreakerMode] = useState(false);
  const sockRef = useRef(null);
  const localTimerRef = useRef(null);
  const isHaupt = user.role === 'haupt';
  // Mit Turnier: der Server trennt die Matten je Turnier (27.09.2026)
  const displayUrl = `${window.location.origin}/display/${matNr}${user.turnier_id ? `?t=${user.turnier_id}` : ''}`;

  useEffect(() => {
    // Relativ zur Seite: in der Produktion reicht nginx /socket.io/ ans
    // Backend weiter, im Entwicklungsbetrieb der Dev-Proxy (src/setupProxy.js,
    // ws: true). Der direkte Weg auf Port 5002 scheitert an CORS.
    // Token im Handshake (26.09.2026): ohne darf der Kanal nur mitlesen —
    // Punkte, Zeit und Ergebnis nimmt der Server nur von angemeldeten
    // Kampfrichtern an (websocket/refereeSocketServer.js).
    const sock = io('/referee', { auth:{ token:user.token }, transports:['websocket','polling'], reconnection:true, reconnectionDelay:1000 });
    sockRef.current = sock;
    sock.on('connect', () => { setConnected(true); sock.emit('join-mat', { matNr, name: user.name, role: user.role }); });
    sock.on('disconnect', () => setConnected(false));
    sock.on('mat-state', s => { setMatState(s); if (s.status==='waiting') { setNoteSubmitted(false); setTiebreakerMode(false); } });
    sock.on('score-update', d => setMatState(p => ({...p,...d})));
    // Server-Ticks synchronisieren den lokalen Timer (korrigieren Drift)
    sock.on('timer-tick', ({remaining}) => setMatState(p => ({...p, timer_remaining:remaining, timer_running:true})));
    sock.on('timer-update', ({running,remaining}) => setMatState(p => ({...p, timer_running:running, timer_remaining:remaining})));
    sock.on('timer-ended', () => setMatState(p => ({...p, timer_running:false, timer_remaining:0})));
    sock.on('kata-update', d => setMatState(p => ({...p, kata_notes:d.kata_notes||{}, kata_average:d.average, kata_label:d.label, ...(d.active_side ? {kata_active_side:d.active_side} : {})})));
    sock.on('bruchtest-update', d => setMatState(p => ({...p, bruchtest_rot_scores:d.rot_scores||[], bruchtest_blau_scores:d.blau_scores||[], bruchtest_rot_total:d.rot_total||0, bruchtest_blau_total:d.blau_total||0, bruchtest_active_side:d.active_side||'rot'})));
    sock.on('result-confirmed', d => setMatState(p => ({...p, status:'finished', winner:d.winner, hinweis:d.hinweis || null})));
    sock.on('referees-update', ({referees}) => setMatState(p => ({...p, connected_referees:referees})));
    sock.on('nicht-berechtigt', ({grund}) => setMatState(p => ({...p, abgelehnt:`Nicht angenommen: ${grund}. Bitte neu anmelden.`})));
    return () => sock.disconnect();
  }, [matNr, user.name, user.role, user.token]);

  // Lokaler Client-Timer: läuft unabhängig, Server-Ticks korrigieren bei Bedarf
  useEffect(() => {
    if (matState.timer_running) {
      if (localTimerRef.current) return; // bereits läuft
      localTimerRef.current = setInterval(() => {
        setMatState(p => {
          if (!p.timer_running || p.timer_remaining <= 0) {
            clearInterval(localTimerRef.current);
            localTimerRef.current = null;
            return p;
          }
          return { ...p, timer_remaining: p.timer_remaining - 1 };
        });
      }, 1000);
    } else {
      if (localTimerRef.current) {
        clearInterval(localTimerRef.current);
        localTimerRef.current = null;
      }
    }
    return () => {
      if (localTimerRef.current) {
        clearInterval(localTimerRef.current);
        localTimerRef.current = null;
      }
    };
  }, [matState.timer_running]); // eslint-disable-line react-hooks/exhaustive-deps

  function emit(event, extra = {}) { sockRef.current?.emit(event, { matNr, ...extra }); }

  function switchMode(newMode) {
    if (!isHaupt) return;
    emit('set-fight', { sessionTyp: newMode, fightInfo: { ...(matState.fight_info||{}), typ: newMode }, timerSeconds: 180 });
    setNoteSubmitted(false); setTiebreakerMode(false);
  }
  function timerAction(action, seconds) {
    if (!isHaupt) return;
    if (action==='start') emit('timer-start');
    else if (action==='stop') emit('timer-stop');
    else emit('timer-reset', { seconds: seconds || matState.timer_seconds || 180 });
  }
  function confirmResult(winner, winMethod) { if (!isHaupt) return; emit('confirm-result', { winner, win_method: winMethod || null }); setTiebreakerMode(false); }
  function nextFight() { if (!isHaupt) return; emit('reset-mat'); setNoteSubmitted(false); setTiebreakerMode(false); }

  const st = matState;
  const isKata      = st.session_typ === 'kata';
  const isBruchtest = st.session_typ === 'bruchtest';
  const isKickboxen = st.session_typ === 'kickboxen';
  const isKumite    = !isKata && !isBruchtest && !isKickboxen;
  const isTied = (isKumite || isKickboxen) && st.rote_punkte === st.blaue_punkte;
  const timeColor = st.timer_remaining <= 30 ? ROT_C : st.timer_remaining <= 60 ? '#f59e0b' : '#fff';

  const MODI = [
    { val:'kumite',    label:'🥊 Kumite',      ac:'rgba(215,162,58,0.2)',  tc:GOLD },
    { val:'kickboxen', label:'🥋 Kickboxen',   ac:'rgba(239,68,68,0.2)',   tc:'#f87171' },
    { val:'kata',      label:'🎋 Kata',         ac:'rgba(139,92,246,0.2)', tc:'#a78bfa' },
    { val:'bruchtest', label:'🧱 Bruchtest',    ac:'rgba(251,146,60,0.2)', tc:ORANGE },
  ];

  return (
    <div style={{ background:'#0a0a14', minHeight:'100vh', fontFamily:'Inter, system-ui, sans-serif', color:'#fff' }}>

      {/* HEADER */}
      <div style={{ background:'rgba(255,255,255,0.04)', borderBottom:`1px solid ${BORDER}`, padding:'0.55rem 0.75rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
        <span style={{fontSize:'1.1rem',flexShrink:0}}>⚖️</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:'0.875rem',lineHeight:1.2}}>Matte {matNr}</div>
          <div style={{fontSize:'0.68rem',color:'rgba(255,255,255,0.4)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            {user.name} · <span style={{color:isHaupt?GOLD:'rgba(255,255,255,0.5)'}}>{isHaupt?'Center':'Judge'}</span>
            {st.fight_info?.division_code && <span style={{marginLeft:'0.4rem',color:'rgba(255,255,255,0.3)'}}>{st.fight_info.division_code}</span>}
          </div>
        </div>
        <button onClick={() => window.open(displayUrl,'_blank')} title="Display öffnen"
          style={{...bs('rgba(255,255,255,0.06)','rgba(255,255,255,0.6)',BORDER),padding:'0.35rem 0.6rem',minHeight:34,fontSize:'1.1rem',borderRadius:9,flexShrink:0}}>📺</button>
        <button onClick={() => { navigator.clipboard?.writeText(displayUrl); }}
          title="Display-Link kopieren"
          style={{...bs('rgba(255,255,255,0.04)','rgba(255,255,255,0.4)',BORDER),padding:'0.3rem 0.55rem',minHeight:32,fontSize:'0.72rem',borderRadius:8,flexShrink:0}}>🔗</button>
        {isHaupt && (
          <button onClick={() => setShowCat(true)}
            style={{...bs('rgba(215,162,58,0.12)',GOLD,'rgba(215,162,58,0.3)'),padding:'0.35rem 0.6rem',minHeight:34,fontSize:'0.78rem',borderRadius:9,flexShrink:0}}>Kat.</button>
        )}
        <button onClick={onLogout}
          style={{...bs('rgba(255,255,255,0.05)','rgba(255,255,255,0.4)',BORDER),padding:'0.35rem 0.55rem',minHeight:34,fontSize:'0.72rem',borderRadius:8,flexShrink:0}}>✕</button>
      </div>

      {/* MODUS-TOGGLE (Center) */}
      {isHaupt && (
        <div style={{ display:'flex', gap:'0.3rem', padding:'0.5rem 0.75rem', borderBottom:`1px solid ${BORDER}`, background:'rgba(0,0,0,0.15)' }}>
          {MODI.map(({val,label,ac,tc}) => (
            <button key={val} onClick={() => switchMode(val)}
              style={{...bs(st.session_typ===val?ac:'rgba(255,255,255,0.04)', st.session_typ===val?tc:'rgba(255,255,255,0.4)', st.session_typ===val?tc:BORDER), flex:1, padding:'0.45rem 0.25rem', fontSize:'0.75rem', minHeight:38, borderRadius:9}}>
              {label}
            </button>
          ))}
          <div style={{display:'flex',alignItems:'center',padding:'0 0.25rem',flexShrink:0}}>
            <div style={{width:7,height:7,borderRadius:'50%',background:connected?GREEN:ROT_C}}/>
          </div>
        </div>
      )}
      {/* Modus-Anzeige (Judge) */}
      {!isHaupt && (
        <div style={{padding:'0.4rem 0.75rem',borderBottom:`1px solid ${BORDER}`,background:'rgba(0,0,0,0.15)',display:'flex',alignItems:'center',gap:'0.75rem',fontSize:'0.8rem'}}>
          <div style={{width:7,height:7,borderRadius:'50%',background:connected?GREEN:ROT_C,flexShrink:0}}/>
          <span style={{color:'rgba(255,255,255,0.5)'}}>
            {isKata?'🎋 Kata':isBruchtest?'🧱 Bruchtest':st.session_typ==='kickboxen'?'🥋 Kickboxen':'🥊 Kumite'}
          </span>
          {st.fight_info?.division_code && <span style={{color:'rgba(255,255,255,0.3)'}}>{st.fight_info.division_code}</span>}
        </div>
      )}

      {/* CONTENT */}
      <div style={{ padding:'0.75rem', maxWidth:520, margin:'0 auto' }}>

        {/* Kämpfernamen */}
        {st.fight_info && (st.fight_info.rot || st.fight_info.blau) && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', marginBottom:'0.6rem' }}>
            {[['rot',ROT_C,'#fca5a5',ROT_BG],['blau',BLAU_C,'#93c5fd',BLAU_BG]].map(([side,col,tc,bg]) => (
              <div key={side} style={{background:bg,border:`1px solid ${col}40`,borderRadius:12,padding:'0.5rem 0.75rem',textAlign:'center'}}>
                <div style={{fontSize:'0.62rem',color:tc,textTransform:'uppercase'}}>{side==='rot'?'Rot':'Blau'}</div>
                <div style={{fontWeight:600,fontSize:'0.875rem',marginTop:'0.1rem'}}>{st.fight_info[side]||'–'}</div>
              </div>
            ))}
          </div>
        )}

        {/* ═══ KUMITE ═══ */}
        {isKumite && <KumitePanel st={st} isHaupt={isHaupt} emit={emit} timerAction={timerAction} confirmResult={confirmResult} nextFight={nextFight} isTied={isTied} tiebreakerMode={tiebreakerMode} setTiebreakerMode={setTiebreakerMode} timeColor={timeColor} />}

        {/* ═══ KICKBOXEN ═══ */}
        {isKickboxen && <KickboxenPanel st={st} isHaupt={isHaupt} emit={emit} timerAction={timerAction} confirmResult={confirmResult} isTied={isTied} tiebreakerMode={tiebreakerMode} setTiebreakerMode={setTiebreakerMode} timeColor={timeColor} />}

        {/* ═══ KATA ═══ */}
        {isKata && <KataPanel isHaupt={isHaupt} sockRef={sockRef} matNr={matNr} kataNote={kataNote} setKataNote={setKataNote} noteSubmitted={noteSubmitted} setNoteSubmitted={setNoteSubmitted} st={st} onConfirm={confirmResult} />}

        {/* Der Server hat eine Eingabe verworfen (Anmeldung abgelaufen, fremdes
            Turnier). Ohne diese Zeile tippt der Kampfrichter weiter ins Leere. */}
        {st.abgelehnt && (
          <div role="alert" style={{background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.5)',borderRadius:10,padding:'0.7rem 0.9rem',marginBottom:'0.6rem',fontSize:'0.9rem',color:'#fecaca'}}>
            {st.abgelehnt}
          </div>
        )}

        {/* ═══ BRUCHTEST ═══ */}
        {isBruchtest && <BruchtestPanel isHaupt={isHaupt} sockRef={sockRef} matNr={matNr} st={st} emit={emit} onConfirm={confirmResult} fightInfo={st.fight_info} katalog={katalog} />}

        {/* Ergebnis-Banner */}
        {st.status === 'finished' && st.winner && (
          <div style={{ background:st.winner==='rot'?ROT_BG:BLAU_BG, border:`1px solid ${st.winner==='rot'?'rgba(239,68,68,0.4)':'rgba(59,130,246,0.4)'}`, borderRadius:16, padding:'1.25rem', textAlign:'center', marginBottom:'0.6rem' }}>
            <div style={{fontSize:'2rem'}}>🏆</div>
            <div style={{fontWeight:900,fontSize:'1.5rem',color:st.winner==='rot'?ROT_C:BLAU_C,textTransform:'uppercase',marginTop:'0.25rem'}}>
              {st.winner==='rot'?'ROT':'BLAU'} gewinnt
            </div>
            {st.fight_info?.[st.winner] && <div style={{color:'rgba(255,255,255,0.6)',fontSize:'0.9rem',marginTop:'0.25rem'}}>{st.fight_info[st.winner]}</div>}
            {/* Was der Server beim Speichern nicht zuordnen konnte. Ohne diese
                Zeile verschwanden Wertungen spurlos, und auf der Matte stand
                trotzdem ein Sieger. */}
            {st.hinweis && (
              <div style={{background:'rgba(251,146,60,0.15)',border:'1px solid rgba(251,146,60,0.45)',borderRadius:10,padding:'0.6rem 0.8rem',marginTop:'0.8rem',fontSize:'0.82rem',color:'#fcd9b0',lineHeight:1.45,textAlign:'left'}}>
                ⚠️ {st.hinweis}
              </div>
            )}
            {isHaupt && <button onClick={nextFight} style={{...bs('rgba(52,211,153,0.2)',GREEN,'rgba(52,211,153,0.4)'),marginTop:'0.9rem',padding:'0.65rem 1.5rem'}}>▶ Nächster Kampf</button>}
          </div>
        )}

        {/* Verbundene */}
        {st.connected_referees?.length > 0 && (
          <div style={{borderTop:`1px solid ${BORDER}`,paddingTop:'0.5rem',display:'flex',gap:'0.4rem',flexWrap:'wrap'}}>
            {st.connected_referees.map((r,i) => (
              <span key={i} style={{fontSize:'0.72rem',padding:'0.2rem 0.55rem',borderRadius:7,background:r.role==='haupt'?'rgba(215,162,58,0.12)':'rgba(255,255,255,0.05)',color:r.role==='haupt'?GOLD:'rgba(255,255,255,0.4)'}}>
                {r.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {showCat && (
        <CategoryPicker krToken={user.token}
          onSelect={(fightInfo, sessionTyp, timerSecs) => {
            sockRef.current?.emit('set-fight', { matNr, sessionTyp, fightInfo, timerSeconds: timerSecs });
            setNoteSubmitted(false); setTiebreakerMode(false); setShowCat(false);
          }}
          onClose={() => setShowCat(false)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  KUMITE PANEL
// ════════════════════════════════════════════════════════════════════════════
function KumitePanel({ st, isHaupt, emit, timerAction, confirmResult, nextFight, isTied, tiebreakerMode, setTiebreakerMode, timeColor }) {
  function score(color, pts, type) { emit('score', { color, points: pts, type }); }
  function undo(color, type) { if (!isHaupt) return; emit('score-undo', { color, type }); }

  return (
    <>
      {/* Timer */}
      <div style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:16, padding:'0.9rem', marginBottom:'0.6rem', textAlign:'center' }}>
        <div style={{ fontSize:'3.75rem', fontWeight:800, letterSpacing:'-0.02em', color:timeColor, fontVariantNumeric:'tabular-nums', lineHeight:1, transition:'color 0.3s' }}>
          {formatTime(st.timer_remaining)}
        </div>
        <div style={{ fontSize:'0.7rem', color:st.timer_running?GREEN:'rgba(255,255,255,0.2)', marginTop:'0.2rem', textTransform:'uppercase', letterSpacing:'0.1em' }}>
          {st.timer_running ? '● Läuft' : st.status==='active' ? '⏸ Pausiert' : '○ Bereit'}
        </div>
        {isHaupt && (
          <div style={{ display:'flex', gap:'0.4rem', justifyContent:'center', marginTop:'0.65rem', flexWrap:'wrap' }}>
            {!st.timer_running
              ? <button onClick={() => timerAction('start')} style={{...bs('rgba(52,211,153,0.2)',GREEN,'rgba(52,211,153,0.4)'),padding:'0.6rem 1.75rem',minHeight:44}}>▶ Start</button>
              : <button onClick={() => timerAction('stop')} style={{...bs('rgba(239,68,68,0.2)','#fca5a5','rgba(239,68,68,0.4)'),padding:'0.6rem 1.75rem',minHeight:44}}>⏸ Stop</button>
            }
            <button onClick={() => timerAction('reset')} style={{...bs('rgba(255,255,255,0.07)','rgba(255,255,255,0.5)',BORDER),padding:'0.6rem 1rem',minHeight:44}}>↺</button>
            {[60,90,120,180].map(s => (
              <button key={s} onClick={() => timerAction('reset',s)} style={{...bs('rgba(255,255,255,0.05)','rgba(255,255,255,0.35)',BORDER),padding:'0.4rem 0.5rem',minHeight:36,fontSize:'0.72rem',borderRadius:8}}>{s}s</button>
            ))}
          </div>
        )}
      </div>

      {/* Punktestand */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:'0.4rem', marginBottom:'0.6rem', alignItems:'stretch' }}>
        {[['rot',ROT_C,'#fca5a5',ROT_BG,'rote_punkte','rote_warnings'],['blau',BLAU_C,'#93c5fd',BLAU_BG,'blaue_punkte','blaue_warnings']].map(([side,col,tc,bg,pk,wk],idx) => (
          <React.Fragment key={side}>
            {idx===1 && <div style={{display:'flex',alignItems:'center',padding:'0 0.2rem'}}><span style={{fontWeight:700,color:'rgba(255,255,255,0.2)',fontSize:'1.5rem'}}>:</span></div>}
            <div style={{background:bg,border:`1px solid ${col}50`,borderRadius:14,padding:'0.75rem',textAlign:'center'}}>
              <div style={{fontSize:'0.65rem',color:tc,textTransform:'uppercase',marginBottom:'0.2rem'}}>{side==='rot'?'ROT':'BLAU'}</div>
              <div style={{fontSize:'3.5rem',fontWeight:900,color:col,lineHeight:1,fontVariantNumeric:'tabular-nums'}}>{st[pk]}</div>
              <div style={{display:'flex',justifyContent:'center',gap:5,marginTop:'0.4rem'}}>
                {[0,1,2].map(i=><div key={i} style={{width:11,height:11,borderRadius:'50%',background:i<st[wk]?col:'rgba(255,255,255,0.1)'}}/>)}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Score Buttons */}
      {[['rot',ROT_C,'#fca5a5',ROT_BG],['blau',BLAU_C,'#93c5fd',BLAU_BG]].map(([side,col,tc,bg]) => (
        <div key={side} style={{marginBottom:'0.5rem'}}>
          <div style={{fontSize:'0.68rem',color:tc,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:'0.3rem'}}>{side==='rot'?'ROT':'BLAU'} – Punkte</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'0.35rem'}}>
            {[[1,'Yuko',0.15],[2,'Waza-ari',0.45],[3,'Ippon',0.75]].map(([pts,name,alpha]) => (
              <button key={pts} onClick={() => score(side,pts,name.toLowerCase())}
                style={{...bs(`rgba(${side==='rot'?'239,68,68':'59,130,246'},${alpha})`,alpha>0.4?'#fff':tc,`${col}50`), flexDirection:'column',gap:'0.15rem',minHeight:56,padding:'0.6rem 0.5rem'}}>
                <span style={{fontSize:'0.875rem'}}>{name}</span>
                <span style={{fontSize:'0.75rem',opacity:0.75}}>+{pts}</span>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Verwarnungen */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.4rem',marginBottom:'0.6rem'}}>
        <button onClick={() => score('rot',0,'penalty')} style={{...bs(ROT_BG,'#fca5a5','rgba(239,68,68,0.3)'),minHeight:44}}>⚠️ Rot – Verwarnung</button>
        <button onClick={() => score('blau',0,'penalty')} style={{...bs(BLAU_BG,'#93c5fd','rgba(59,130,246,0.3)'),minHeight:44}}>⚠️ Blau – Verwarnung</button>
      </div>

      {/* Undo (Center only) */}
      {isHaupt && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'0.35rem',marginBottom:'0.6rem'}}>
          {[['↩ Rot Pkt','rot','score'],['↩ Blau Pkt','blau','score'],['↩ Rot ⚠','rot','penalty'],['↩ Blau ⚠','blau','penalty']].map(([l,c,t]) => (
            <button key={l} onClick={() => undo(c,t)} style={{...bs('rgba(255,255,255,0.04)','rgba(255,255,255,0.4)',BORDER),padding:'0.4rem 0.25rem',fontSize:'0.68rem',minHeight:38,borderRadius:9}}>{l}</button>
          ))}
        </div>
      )}

      {/* Ergebnis */}
      {isHaupt && st.status !== 'finished' && (
        tiebreakerMode ? (
          <div style={{background:'rgba(215,162,58,0.08)',border:'1px solid rgba(215,162,58,0.3)',borderRadius:14,padding:'1rem',marginBottom:'0.6rem'}}>
            <div style={{textAlign:'center',fontWeight:700,color:GOLD,marginBottom:'0.75rem'}}>⚖️ Gleichstand – Hantei-Entscheidung</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem',marginBottom:'0.5rem'}}>
              <button onClick={() => confirmResult('rot')} style={{...bs('rgba(239,68,68,0.3)','#fff','rgba(239,68,68,0.5)'),minHeight:56,fontSize:'1rem',flexDirection:'column',gap:'0.2rem'}}>
                <span>🏆 ROT</span><span style={{fontSize:'0.75em',opacity:0.7}}>gewinnt</span>
              </button>
              <button onClick={() => confirmResult('blau')} style={{...bs('rgba(59,130,246,0.3)','#fff','rgba(59,130,246,0.5)'),minHeight:56,fontSize:'1rem',flexDirection:'column',gap:'0.2rem'}}>
                <span>🏆 BLAU</span><span style={{fontSize:'0.75em',opacity:0.7}}>gewinnt</span>
              </button>
            </div>
            <button onClick={() => setTiebreakerMode(false)} style={{...bs('rgba(255,255,255,0.05)','rgba(255,255,255,0.4)',BORDER),width:'100%'}}>Abbrechen</button>
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0.4rem',marginBottom:'0.6rem'}}>
            <button onClick={() => confirmResult('rot')} style={{...bs(ROT_BG,'#fca5a5','rgba(239,68,68,0.35)'),minHeight:48,flexDirection:'column',gap:'0.15rem',fontSize:'0.82rem'}}>
              <span>🏆</span><span>Rot gewinnt</span>
            </button>
            <button onClick={() => { isTied?setTiebreakerMode(true):confirmResult(st.rote_punkte>st.blaue_punkte?'rot':'blau'); }}
              style={{...bs(isTied?'rgba(215,162,58,0.12)':'rgba(52,211,153,0.12)',isTied?GOLD:GREEN,isTied?'rgba(215,162,58,0.35)':'rgba(52,211,153,0.35)'),minHeight:48,flexDirection:'column',gap:'0.15rem',fontSize:'0.82rem'}}>
              <span>{isTied?'⚖️':'✓'}</span><span>{isTied?'Entscheidung':'Ergebnis'}</span>
            </button>
            <button onClick={() => confirmResult('blau')} style={{...bs(BLAU_BG,'#93c5fd','rgba(59,130,246,0.35)'),minHeight:48,flexDirection:'column',gap:'0.15rem',fontSize:'0.82rem'}}>
              <span>🏆</span><span>Blau gewinnt</span>
            </button>
          </div>
        )
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  KATA PANEL
// ════════════════════════════════════════════════════════════════════════════
function KataPanel({ isHaupt, sockRef, matNr, kataNote, setKataNote, noteSubmitted, setNoteSubmitted, st, onConfirm }) {
  const presets = [5.0,5.5,6.0,6.5,7.0,7.5,8.0,8.5,9.0,9.5,9.7,10.0];

  // Steht auf der aktiven Seite ueberhaupt jemand? Wertungen auf einer leeren
  // Seite kann der Server niemandem zuordnen -- sie waren beim Bestaetigen
  // spurlos weg, obwohl die App brav den Durchschnitt zeigte.
  const seite = st.kata_active_side || 'rot';
  const fi = st.fight_info || {};
  const wkAufSeite = seite === 'blau' ? fi.blau_wettkampfer_id : fi.rot_wettkampfer_id;
  const nameAufSeite = seite === 'blau' ? fi.blau : fi.rot;
  const seiteLeer = !wkAufSeite;

  return (
    <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:16,padding:'1rem',marginBottom:'0.6rem'}}>
      <div style={{textAlign:'center',marginBottom:'0.75rem'}}>
        <div style={{fontSize:'0.7rem',color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'0.07em'}}>Meine Wertung</div>
        <div style={{fontSize:'4rem',fontWeight:900,color:GOLD,lineHeight:1,marginTop:'0.25rem'}}>{kataNote.toFixed(1)}</div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:'0.3rem',marginBottom:'0.65rem'}}>
        {presets.map(n => {
          const active = Math.abs(kataNote-n)<0.01;
          return <button key={n} onClick={() => {setKataNote(n);setNoteSubmitted(false);}} style={{...bs(active?GOLD:'rgba(255,255,255,0.07)',active?'#000':'rgba(255,255,255,0.75)',active?GOLD:BORDER),padding:'0.5rem 0.2rem',fontSize:'0.78rem',minHeight:42,borderRadius:9}}>{n.toFixed(1)}</button>;
        })}
      </div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.75rem',marginBottom:'0.65rem'}}>
        <button onClick={() => {setKataNote(n=>Math.max(0,Math.round((n-0.1)*10)/10));setNoteSubmitted(false);}} style={{...bs('rgba(255,255,255,0.08)','#fff',BORDER),width:52,height:52,padding:0,fontSize:'1.4rem',borderRadius:12}}>−</button>
        <span style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.4)',minWidth:50,textAlign:'center'}}>±0.1</span>
        <button onClick={() => {setKataNote(n=>Math.min(10,Math.round((n+0.1)*10)/10));setNoteSubmitted(false);}} style={{...bs('rgba(255,255,255,0.08)','#fff',BORDER),width:52,height:52,padding:0,fontSize:'1.4rem',borderRadius:12}}>+</button>
      </div>
      {seiteLeer && (
        <div style={{background:'rgba(251,146,60,0.12)',border:'1px solid rgba(251,146,60,0.4)',borderRadius:10,padding:'0.6rem 0.75rem',marginBottom:'0.6rem',fontSize:'0.82rem',color:'#fcd9b0',lineHeight:1.4}}>
          Auf der Seite <strong>{seite === 'blau' ? 'BLAU' : 'ROT'}</strong> steht kein Wettkämpfer.
          {isHaupt ? ' Erst „Jetzt am Start“ umstellen oder den Kämpfer setzen.' : ' Der Hauptrichter muss die Seite umstellen.'}
        </div>
      )}
      <button
        disabled={seiteLeer}
        onClick={() => { if (seiteLeer) return; sockRef.current?.emit('kata-note',{matNr,note:kataNote}); setNoteSubmitted(true); }}
        style={{...bs(seiteLeer?'rgba(255,255,255,0.06)':(noteSubmitted?'rgba(52,211,153,0.2)':GOLD),seiteLeer?'rgba(255,255,255,0.3)':(noteSubmitted?GREEN:'#000'),seiteLeer?BORDER:(noteSubmitted?'rgba(52,211,153,0.4)':GOLD)),width:'100%',fontSize:'1rem',marginBottom:noteSubmitted?'0.75rem':0,cursor:seiteLeer?'not-allowed':'pointer'}}>
        {seiteLeer ? 'Keine Wertung möglich' : (noteSubmitted?`✓ Abgegeben (${kataNote.toFixed(1)})`:`Wertung abgeben${nameAufSeite ? ` — ${nameAufSeite}` : ''}`)}
      </button>
      {Object.keys(st.kata_notes||{}).length > 0 && (
        <div style={{marginTop:'0.75rem',paddingTop:'0.65rem',borderTop:`1px solid ${BORDER}`}}>
          <div style={{fontSize:'0.68rem',color:'rgba(255,255,255,0.35)',textTransform:'uppercase',marginBottom:'0.35rem'}}>Alle Wertungen</div>
          {Object.values(st.kata_notes).map((n,i) => (
            <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'0.2rem 0',fontSize:'0.875rem'}}>
              <span style={{color:'rgba(255,255,255,0.55)'}}>{n.name}</span>
              <span style={{fontWeight:700,color:GOLD}}>{(+n.note).toFixed(1)}</span>
            </div>
          ))}
          {st.kata_average && (
            <div style={{display:'flex',justifyContent:'space-between',paddingTop:'0.35rem',marginTop:'0.25rem',borderTop:`1px solid ${BORDER}`,fontWeight:700,fontSize:'1rem'}}>
              <span style={{color:'rgba(255,255,255,0.65)'}}>{st.kata_label || 'Wertung'}</span>
              <span style={{color:GREEN}}>{st.kata_average}</span>
            </div>
          )}
        </div>
      )}
      {isHaupt && st.status !== 'finished' && (
        <>
          <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginTop:'0.65rem',padding:'0.5rem 0.65rem',background:'rgba(255,255,255,0.04)',borderRadius:10,border:'1px solid rgba(255,255,255,0.1)'}}>
            <span style={{fontSize:'0.72rem',color:'rgba(255,255,255,0.4)',flex:1}}>Jetzt am Start:</span>
            <button onClick={() => { sockRef.current?.emit('kata-set-side',{matNr,side:'rot'}); }}
              style={{...bs((st.kata_active_side||'rot')==='rot'?ROT_BG:'rgba(255,255,255,0.06)','#fca5a5','rgba(239,68,68,0.3)'),padding:'0.25rem 0.75rem',fontSize:'0.78rem',minHeight:30}}>
              🔴 ROT
            </button>
            <button onClick={() => { sockRef.current?.emit('kata-set-side',{matNr,side:'blau'}); }}
              style={{...bs((st.kata_active_side||'rot')==='blau'?BLAU_BG:'rgba(255,255,255,0.06)','#93c5fd','rgba(59,130,246,0.3)'),padding:'0.25rem 0.75rem',fontSize:'0.78rem',minHeight:30}}>
              🔵 BLAU
            </button>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.4rem',marginTop:'0.5rem'}}>
            <button onClick={() => onConfirm('rot')} style={{...bs(ROT_BG,'#fca5a5','rgba(239,68,68,0.35)'),minHeight:44}}>🏆 Rot gewinnt</button>
            <button onClick={() => onConfirm('blau')} style={{...bs(BLAU_BG,'#93c5fd','rgba(59,130,246,0.35)'),minHeight:44}}>🏆 Blau gewinnt</button>
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  BRUCHTEST PANEL – vollständig mit Techniken, Material, Kriterien
// ════════════════════════════════════════════════════════════════════════════
const BRUCHTEST_FORM_INIT = {
  technikCat: 'hand',
  technik: null,
  material: null,          // wird gesetzt, sobald der Katalog da ist
  anzahl: 3,
  sprung: false,
  schwierigkeit: 7,
  technikWert: 7,          // vierte Note, seit 15.09. Teil der Rechnung
  ausfuehrung: 7,
  kraft: 7,
  versuche: 1,
  erfolg: true,
};

function BruchtestPanel({ isHaupt, sockRef, matNr, st, emit, onConfirm, fightInfo, katalog }) {
  const [form, setForm] = useState(BRUCHTEST_FORM_INIT);
  const [activeSide, setActiveSide] = useState(st.bruchtest_active_side || 'rot');

  // Sync active side from server
  useEffect(() => {
    if (st.bruchtest_active_side) setActiveSide(st.bruchtest_active_side);
  }, [st.bruchtest_active_side]);

  // Nur die Vorschau -- gerechnet und gespeichert wird am Server.
  // Ohne Technik und Material steht 0: sonst zeigt die Anzeige eine Zahl fuer
  // eine Wertung, die es noch gar nicht gibt.
  const punkte = (!form.technik || !form.material) ? 0 : vorschauPunkte(
    {
      anzahl: form.anzahl,
      material: form.material?.id,
      mitSprung: form.sprung,
      schwierigkeit: form.schwierigkeit,
      technikWert: form.technikWert,
      ausfuehrung: form.ausfuehrung,
      kraft: form.kraft,
      versuche: form.versuche,
      erfolg: form.erfolg,
    },
    form.technik,
    { materialien: katalog?.materialien || [], sprungMultiplikatoren: katalog?.sprungMultiplikatoren || {} },
    katalog?.modus || 'points'
  );
  const rotTotal = st.bruchtest_rot_total || (st.bruchtest_rot_scores||[]).reduce((s,e)=>s+(e.punkte||0),0);
  const blauTotal = st.bruchtest_blau_total || (st.bruchtest_blau_scores||[]).reduce((s,e)=>s+(e.punkte||0),0);

  function setSide(side) {
    setActiveSide(side);
    if (isHaupt) emit('bruchtest-set-side', { side });
  }

  function submitScore() {
    if (!form.technik || !form.material) return;
    // Geschickt werden die Eingaben, nicht das Ergebnis: die Punkte rechnet
    // der Server neu (websocket/refereeSocketServer.js). `punkte` faehrt nur
    // als Vorschau mit, falls die Anzeige sie vor der Antwort braucht.
    const entry = {
      technikKey: form.technik.id,
      technikName: form.technik.name,
      technikMult: form.technik.multiplikator,
      technikCat: form.technik.kategorie,
      materialKey: form.material.id,
      materialName: form.material.name,
      materialPts: form.material.basisPunkte,
      anzahl: form.anzahl,
      sprung: form.sprung,
      schwierigkeit: form.schwierigkeit,
      technikWert: form.technikWert,
      ausfuehrung: form.ausfuehrung,
      kraft: form.kraft,
      versuche: form.versuche,
      erfolg: form.erfolg,
      punkte,
    };
    sockRef.current?.emit('bruchtest-score', { matNr, side: activeSide, entry });
    // Reset form für nächste Technik
    setForm(prev => ({ ...BRUCHTEST_FORM_INIT, technikCat: prev.technikCat }));
  }

  function undoLast() {
    if (!isHaupt) return;
    emit('bruchtest-undo', { side: activeSide });
  }

  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));
  // useMemo, damit die Listen zwischen zwei Rendern dieselben bleiben --
  // sonst feuert der Effekt darunter bei jedem Rendern.
  const alleTechniken = useMemo(() => katalog?.techniken || [], [katalog]);
  const materialien = useMemo(() => katalog?.materialien || [], [katalog]);
  const sprungTabelle = useMemo(() => katalog?.sprungMultiplikatoren || {}, [katalog]);
  const curTechniken = alleTechniken.filter(t => t.kategorie === form.technikCat);
  const sprungFaktor = form.technik ? (sprungTabelle[form.technik.kategorie] || 1) : 1;

  // Sobald der Katalog da ist, das erste Material vorbelegen.
  useEffect(() => {
    if (!form.material && materialien.length) setForm(p => ({ ...p, material: materialien[0] }));
  }, [materialien, form.material]);

  // Slider-Buttons 1-10
  const SliderRow = ({ label, field, val }) => (
    <div style={{ marginBottom: '0.5rem' }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.25rem' }}>
        <span style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</span>
        <span style={{ fontSize:'0.875rem', fontWeight:700, color:ORANGE }}>{val}</span>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(10,1fr)', gap:'0.2rem' }}>
        {[1,2,3,4,5,6,7,8,9,10].map(n => (
          <button key={n} onClick={() => f(field, n)}
            style={{ background:val===n?ORANGE:'rgba(255,255,255,0.07)', color:val===n?'#000':'rgba(255,255,255,0.6)', border:`1px solid ${val===n?ORANGE:BORDER}`, borderRadius:7, padding:'0.35rem 0.1rem', fontSize:'0.72rem', cursor:'pointer', fontFamily:'inherit', fontWeight:val===n?700:400, minHeight:32, textAlign:'center', outline:'none', touchAction:'manipulation' }}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  const otherSide = activeSide === 'rot' ? 'blau' : 'rot';
  const activeCol  = activeSide === 'rot' ? ROT_C  : BLAU_C;
  const activeTc   = activeSide === 'rot' ? '#fca5a5' : '#93c5fd';
  const activeBg   = activeSide === 'rot' ? ROT_BG  : BLAU_BG;
  const otherCol   = otherSide  === 'rot' ? ROT_C   : BLAU_C;
  const activeTotal = activeSide === 'rot' ? rotTotal : blauTotal;
  const otherTotal  = otherSide  === 'rot' ? rotTotal : blauTotal;
  const activeScores = activeSide === 'rot' ? (st.bruchtest_rot_scores||[]) : (st.bruchtest_blau_scores||[]);
  const otherScores  = otherSide  === 'rot' ? (st.bruchtest_rot_scores||[]) : (st.bruchtest_blau_scores||[]);

  return (
    <div style={{ fontFamily:'Inter, system-ui, sans-serif' }}>

      {/* ── Aktueller Starter ── */}
      <div style={{ background:activeBg, border:`2px solid ${activeCol}60`, borderRadius:18, padding:'1rem', marginBottom:'0.6rem', textAlign:'center' }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:'0.35rem', background:`${activeCol}20`, border:`1px solid ${activeCol}40`, borderRadius:20, padding:'0.2rem 0.65rem', marginBottom:'0.4rem' }}>
          <span style={{ width:8, height:8, borderRadius:'50%', background:activeCol, display:'inline-block' }}/>
          <span style={{ fontSize:'0.65rem', color:activeTc, textTransform:'uppercase', letterSpacing:'0.1em', fontWeight:700 }}>Jetzt am Start</span>
        </div>
        <div style={{ fontSize:'1.4rem', fontWeight:800, color:'#fff', marginBottom:'0.15rem', lineHeight:1.2 }}>
          {fightInfo?.[activeSide] || '–'}
        </div>
        <div style={{ fontSize:'3.5rem', fontWeight:900, color:activeCol, lineHeight:1, fontVariantNumeric:'tabular-nums' }}>
          {activeTotal.toFixed(1)}
        </div>
        <div style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.3)', marginTop:'0.2rem' }}>
          {activeScores.length} {activeScores.length === 1 ? 'Technik' : 'Techniken'}
        </div>
        {/* Weiter-Button (Center only) */}
        {isHaupt && (
          <button onClick={() => setSide(otherSide)}
            style={{ ...bs('rgba(255,255,255,0.1)','#fff',BORDER), marginTop:'0.75rem', padding:'0.5rem 1.25rem', fontSize:'0.82rem', borderRadius:10 }}>
            → Weiter: {fightInfo?.[otherSide] || (otherSide==='rot'?'ROT':'BLAU')}
          </button>
        )}
      </div>

      {/* Andere Seite – kompakte Zusammenfassung (wenn schon Techniken) */}
      {otherScores.length > 0 && (
        <div style={{ background:SURFACE, border:`1px solid ${otherCol}30`, borderRadius:12, padding:'0.6rem 0.875rem', marginBottom:'0.6rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontSize:'0.82rem', color:'#fff', fontWeight:600 }}>
            {fightInfo?.[otherSide] || (otherSide==='rot'?'ROT':'BLAU')}
            <span style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', fontWeight:400, marginLeft:'0.4rem' }}>{otherScores.length} Techniken</span>
          </div>
          <div style={{ fontSize:'1.25rem', fontWeight:800, color:otherCol }}>{otherTotal.toFixed(1)} Pkt</div>
        </div>
      )}

      {/* ── Technik-Eingabe ── */}
      <div style={{ background:SURFACE, border:`1px solid ${activeCol}40`, borderRadius:16, padding:'1rem', marginBottom:'0.6rem' }}>
        <div style={{ fontSize:'0.72rem', color:activeTc, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'0.75rem' }}>
          + Neue Technik für {fightInfo?.[activeSide] || (activeSide==='rot'?'ROT':'BLAU')}
        </div>

        {/* Technik-Kategorie */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.3rem', marginBottom:'0.6rem' }}>
          {[['hand','✊ Hand'],['fuss','🦵 Fuß'],['spezial','⚡ Spezial']].map(([cat,lbl]) => (
            <button key={cat} onClick={() => { f('technikCat',cat); f('technik',null); }}
              style={{...bs(form.technikCat===cat?'rgba(251,146,60,0.2)':'rgba(255,255,255,0.05)', form.technikCat===cat?ORANGE:'rgba(255,255,255,0.45)', form.technikCat===cat?ORANGE:BORDER), minHeight:38, fontSize:'0.78rem', borderRadius:9}}>
              {lbl}
            </button>
          ))}
        </div>

        {/* Technik-Auswahl */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:'0.3rem', marginBottom:'0.75rem' }}>
          {curTechniken.map(t => {
            const active = form.technik?.id === t.id;
            return (
              <button key={t.id} onClick={() => f('technik', t)}
                style={{ background:active?ORANGE:'rgba(255,255,255,0.07)', color:active?'#000':'rgba(255,255,255,0.75)', border:`1px solid ${active?ORANGE:BORDER}`, borderRadius:9, padding:'0.4rem 0.75rem', fontSize:'0.8rem', cursor:'pointer', fontFamily:'inherit', fontWeight:active?700:400, outline:'none', touchAction:'manipulation' }}>
                {t.name}
                <span style={{ fontSize:'0.7em', opacity:0.7, marginLeft:'0.3rem' }}>×{Number(t.multiplikator).toFixed(1)}</span>
              </button>
            );
          })}
        </div>

        {/* Material */}
        <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.35rem' }}>Material</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'0.3rem', marginBottom:'0.75rem' }}>
          {materialien.map(m => {
            const active = form.material?.id === m.id;
            return (
              <button key={m.id} onClick={() => f('material', m)}
                style={{...bs(active?'rgba(251,146,60,0.2)':'rgba(255,255,255,0.05)', active?ORANGE:'rgba(255,255,255,0.55)', active?ORANGE:BORDER), flexDirection:'column', gap:'0.1rem', minHeight:54, padding:'0.5rem 0.25rem', fontSize:'0.75rem', borderRadius:10}}>
                <span>{m.name}</span>
                <span style={{fontSize:'0.65em',opacity:0.6}}>{m.basisPunkte} Pkt</span>
              </button>
            );
          })}
        </div>

        {/* Anzahl + Sprung + Versuche + Erfolg */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', marginBottom:'0.75rem' }}>
          {/* Anzahl */}
          <div>
            <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.35rem' }}>Anzahl</div>
            <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
              <button onClick={() => f('anzahl',Math.max(1,form.anzahl-1))} style={{...bs('rgba(255,255,255,0.07)','#fff',BORDER),width:40,height:40,padding:0,fontSize:'1.2rem',borderRadius:9,flexShrink:0}}>−</button>
              <span style={{fontSize:'1.5rem',fontWeight:700,flex:1,textAlign:'center'}}>{form.anzahl}</span>
              <button onClick={() => f('anzahl',form.anzahl+1)} style={{...bs('rgba(255,255,255,0.07)','#fff',BORDER),width:40,height:40,padding:0,fontSize:'1.2rem',borderRadius:9,flexShrink:0}}>+</button>
            </div>
          </div>
          {/* Sprung */}
          <div>
            <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.35rem' }}>
              Sprung {form.sprung && form.technik ? `(×${sprungFaktor.toFixed(1)})` : ''}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.3rem' }}>
              <button onClick={() => f('sprung',false)} style={{...bs(!form.sprung?'rgba(255,255,255,0.12)':'rgba(255,255,255,0.05)',!form.sprung?'#fff':'rgba(255,255,255,0.45)',BORDER),minHeight:40,borderRadius:9}}>Nein</button>
              <button onClick={() => f('sprung',true)} style={{...bs(form.sprung?'rgba(251,146,60,0.25)':'rgba(255,255,255,0.05)',form.sprung?ORANGE:'rgba(255,255,255,0.45)',form.sprung?ORANGE:BORDER),minHeight:40,borderRadius:9}}>Ja 🚀</button>
            </div>
          </div>
          {/* Versuche */}
          <div>
            <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.35rem' }}>
              Versuche {form.versuche>1?`(−${Math.min(100,(form.versuche-1)*20)}%)`:''}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
              <button onClick={() => f('versuche',Math.max(1,form.versuche-1))} style={{...bs('rgba(255,255,255,0.07)','#fff',BORDER),width:40,height:40,padding:0,fontSize:'1.2rem',borderRadius:9,flexShrink:0}}>−</button>
              <span style={{fontSize:'1.5rem',fontWeight:700,flex:1,textAlign:'center',color:form.versuche>1?'#fca5a5':'#fff'}}>{form.versuche}</span>
              <button onClick={() => f('versuche',form.versuche+1)} style={{...bs('rgba(255,255,255,0.07)','#fff',BORDER),width:40,height:40,padding:0,fontSize:'1.2rem',borderRadius:9,flexShrink:0}}>+</button>
            </div>
          </div>
          {/* Erfolg */}
          <div>
            <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.35rem' }}>Erfolg</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.3rem' }}>
              <button onClick={() => f('erfolg',true)} style={{...bs(form.erfolg?'rgba(52,211,153,0.2)':'rgba(255,255,255,0.05)',form.erfolg?GREEN:'rgba(255,255,255,0.45)',form.erfolg?'rgba(52,211,153,0.4)':BORDER),minHeight:40,borderRadius:9}}>✓ Ja</button>
              <button onClick={() => f('erfolg',false)} style={{...bs(!form.erfolg?'rgba(239,68,68,0.2)':'rgba(255,255,255,0.05)',!form.erfolg?'#fca5a5':'rgba(255,255,255,0.45)',!form.erfolg?'rgba(239,68,68,0.4)':BORDER),minHeight:40,borderRadius:9}}>✗ Nein</button>
            </div>
          </div>
        </div>

        {/* Bewertungs-Slider */}
        <SliderRow label="Schwierigkeitsgrad" field="schwierigkeit" val={form.schwierigkeit} />
        <SliderRow label="Technik" field="technikWert" val={form.technikWert} />
        <SliderRow label="Ausführung" field="ausfuehrung" val={form.ausfuehrung} />
        <SliderRow label="Kraft" field="kraft" val={form.kraft} />

        {/* Punkte-Vorschau + Submit */}
        <div style={{ background:'rgba(0,0,0,0.3)', borderRadius:12, padding:'0.75rem 1rem', marginBottom:'0.65rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.4)', textTransform:'uppercase' }}>Punkte diese Technik</div>
            <div style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.3)', marginTop:'0.1rem' }}>
              {form.technik && form.material ? (
                `${form.material.basisPunkte}×${form.anzahl}×${form.technik.multiplikator}${form.sprung?`×${sprungFaktor.toFixed(1)}`:''}×(${form.schwierigkeit}/10)×(${form.technikWert}/10)×(${form.ausfuehrung}/10)×(${form.kraft}/10)${form.versuche>1?`×${(Math.max(0,1-(form.versuche-1)*0.2)).toFixed(1)}`:''}`
              ) : '— Technik & Material wählen'}
            </div>
          </div>
          <div style={{ fontSize:'2.2rem', fontWeight:900, color: form.erfolg && form.technik && form.material ? ORANGE : 'rgba(255,255,255,0.2)', fontVariantNumeric:'tabular-nums' }}>
            {punkte.toFixed(2)}
          </div>
        </div>

        <button onClick={submitScore} disabled={!form.technik || !form.material}
          style={{ ...bs(form.technik&&form.material?ORANGE:'rgba(255,255,255,0.08)', form.technik&&form.material?'#000':'rgba(255,255,255,0.2)', form.technik&&form.material?ORANGE:BORDER), width:'100%', fontSize:'1rem', opacity: form.technik&&form.material?1:0.5 }}>
          + Technik hinzufügen
        </button>
      </div>

      {/* ── Techniken des aktuellen Starters ── */}
      {activeScores.length > 0 && (
        <div style={{ background:SURFACE, border:`1px solid ${activeCol}30`, borderRadius:14, padding:'0.75rem 1rem', marginBottom:'0.6rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
            <div style={{ fontSize:'0.72rem', color:activeTc, textTransform:'uppercase', letterSpacing:'0.05em' }}>
              Techniken – {fightInfo?.[activeSide] || (activeSide==='rot'?'ROT':'BLAU')}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
              <span style={{ fontSize:'1.1rem', fontWeight:800, color:activeCol }}>{activeTotal.toFixed(2)}</span>
              {isHaupt && (
                <button onClick={undoLast} style={{...bs('rgba(255,255,255,0.05)','rgba(255,255,255,0.4)',BORDER),padding:'0.25rem 0.6rem',minHeight:28,fontSize:'0.7rem',borderRadius:7}}>↩ Undo</button>
              )}
            </div>
          </div>
          {activeScores.map((e, i) => (
            <div key={e.id||i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.3rem 0', borderTop: i>0?`1px solid ${BORDER}`:'none', fontSize:'0.8rem' }}>
              <div>
                <span style={{ fontWeight:600 }}>{e.technikName}</span>
                <span style={{ color:'rgba(255,255,255,0.35)', marginLeft:'0.4rem', fontSize:'0.72rem' }}>
                  {e.anzahl}× {e.materialName}{e.sprung?' 🚀':''}{!e.erfolg?' ✗':''}{e.versuche>1?` (${e.versuche}×)`:''} — ×{e.technikMult}
                </span>
              </div>
              <span style={{ fontWeight:700, color:e.erfolg?ORANGE:'rgba(255,255,255,0.3)', flexShrink:0, marginLeft:'0.5rem' }}>
                {e.punkte.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Ergebnis-Vergleich (beide haben Techniken) ── */}
      {rotTotal > 0 && blauTotal > 0 && st.status !== 'finished' && (
        <div style={{ background:'rgba(0,0,0,0.25)', border:`1px solid ${BORDER}`, borderRadius:16, padding:'0.875rem 1rem', marginBottom:'0.6rem' }}>
          <div style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.6rem', textAlign:'center' }}>Zwischenstand</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:'0.5rem', alignItems:'center', marginBottom:'0.75rem' }}>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'0.8rem', fontWeight:600, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{fightInfo?.rot||'ROT'}</div>
              <div style={{ fontSize:'2rem', fontWeight:900, color: rotTotal >= blauTotal ? ROT_C : 'rgba(239,68,68,0.45)', fontVariantNumeric:'tabular-nums' }}>{rotTotal.toFixed(1)}</div>
            </div>
            <span style={{ color:'rgba(255,255,255,0.2)', fontWeight:700 }}>:</span>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'0.8rem', fontWeight:600, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{fightInfo?.blau||'BLAU'}</div>
              <div style={{ fontSize:'2rem', fontWeight:900, color: blauTotal >= rotTotal ? BLAU_C : 'rgba(59,130,246,0.45)', fontVariantNumeric:'tabular-nums' }}>{blauTotal.toFixed(1)}</div>
            </div>
          </div>
          {isHaupt && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.4rem' }}>
              <button onClick={() => onConfirm('rot')} style={{ ...bs(ROT_BG,'#fca5a5','rgba(239,68,68,0.35)'), minHeight:44, flexDirection:'column', gap:'0.1rem', fontSize:'0.82rem' }}>
                <span>🏆 ROT gewinnt</span><span style={{ fontSize:'0.72em', opacity:0.6 }}>{rotTotal.toFixed(1)} Pkt</span>
              </button>
              <button onClick={() => onConfirm('blau')} style={{ ...bs(BLAU_BG,'#93c5fd','rgba(59,130,246,0.35)'), minHeight:44, flexDirection:'column', gap:'0.1rem', fontSize:'0.82rem' }}>
                <span>🏆 BLAU gewinnt</span><span style={{ fontSize:'0.72em', opacity:0.6 }}>{blauTotal.toFixed(1)} Pkt</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Ergebnis-Buttons wenn nur einer gestartet hat (Center) */}
      {isHaupt && st.status !== 'finished' && !(rotTotal > 0 && blauTotal > 0) && (rotTotal > 0 || blauTotal > 0) && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', marginBottom:'0.6rem' }}>
          <button onClick={() => onConfirm('rot')} style={{ ...bs(ROT_BG,'#fca5a5','rgba(239,68,68,0.35)'), minHeight:48, flexDirection:'column', gap:'0.15rem' }}>
            <span>🏆 ROT gewinnt</span><span style={{ fontSize:'0.75em', opacity:0.7 }}>{rotTotal.toFixed(1)} Pkt</span>
          </button>
          <button onClick={() => onConfirm('blau')} style={{ ...bs(BLAU_BG,'#93c5fd','rgba(59,130,246,0.35)'), minHeight:48, flexDirection:'column', gap:'0.15rem' }}>
            <span>🏆 BLAU gewinnt</span><span style={{ fontSize:'0.75em', opacity:0.7 }}>{blauTotal.toFixed(1)} Pkt</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  KICKBOXEN PANEL
// ════════════════════════════════════════════════════════════════════════════
function KickboxenPanel({ st, isHaupt, emit, timerAction, confirmResult, isTied, tiebreakerMode, setTiebreakerMode, timeColor }) {
  function score(color, pts) { emit('score', { color, points: pts, type: 'kick' }); }
  function undo(color)       { if (!isHaupt) return; emit('score-undo', { color, type: 'kick' }); }
  function warn(color)       { emit('score', { color, points: 0, type: 'penalty' }); }
  function undoWarn(color)   { if (!isHaupt) return; emit('score-undo', { color, type: 'penalty' }); }

  // 3 Verwarnungen = DQ (Kickboxen-Regel)
  const rotDQ  = st.rote_warnings  >= 3;
  const blauDQ = st.blaue_warnings >= 3;

  return (
    <>
      {/* Timer */}
      <div style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:16, padding:'0.9rem', marginBottom:'0.6rem', textAlign:'center' }}>
        <div style={{ fontSize:'3.75rem', fontWeight:800, letterSpacing:'-0.02em', color:timeColor, fontVariantNumeric:'tabular-nums', lineHeight:1, transition:'color 0.3s' }}>
          {formatTime(st.timer_remaining)}
        </div>
        <div style={{ fontSize:'0.7rem', color:st.timer_running?GREEN:'rgba(255,255,255,0.2)', marginTop:'0.2rem', textTransform:'uppercase', letterSpacing:'0.1em' }}>
          {st.timer_running ? '● Läuft' : st.status==='active' ? '⏸ Pausiert' : '○ Bereit'}
        </div>
        {isHaupt && (
          <div style={{ display:'flex', gap:'0.4rem', justifyContent:'center', marginTop:'0.65rem', flexWrap:'wrap' }}>
            {!st.timer_running
              ? <button onClick={() => timerAction('start')} style={{...bs('rgba(52,211,153,0.2)',GREEN,'rgba(52,211,153,0.4)'),padding:'0.6rem 1.75rem',minHeight:44}}>▶ Start</button>
              : <button onClick={() => timerAction('stop')}  style={{...bs('rgba(239,68,68,0.2)','#fca5a5','rgba(239,68,68,0.4)'),padding:'0.6rem 1.75rem',minHeight:44}}>⏸ Stop</button>
            }
            <button onClick={() => timerAction('reset')} style={{...bs('rgba(255,255,255,0.07)','rgba(255,255,255,0.5)',BORDER),padding:'0.6rem 1rem',minHeight:44}}>↺</button>
            {[60,90,120,180].map(s => (
              <button key={s} onClick={() => timerAction('reset',s)} style={{...bs('rgba(255,255,255,0.05)','rgba(255,255,255,0.35)',BORDER),padding:'0.4rem 0.5rem',minHeight:36,fontSize:'0.72rem',borderRadius:8}}>{s}s</button>
            ))}
          </div>
        )}
      </div>

      {/* Punktestand */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', gap:'0.4rem', marginBottom:'0.6rem', alignItems:'stretch' }}>
        {[['rot',ROT_C,'#fca5a5',ROT_BG,'rote_punkte','rote_warnings'],['blau',BLAU_C,'#93c5fd',BLAU_BG,'blaue_punkte','blaue_warnings']].map(([side,col,tc,bg,pk,wk],idx) => {
          const isDQ = side==='rot' ? rotDQ : blauDQ;
          return (
            <React.Fragment key={side}>
              {idx===1 && <div style={{display:'flex',alignItems:'center',padding:'0 0.2rem'}}><span style={{fontWeight:700,color:'rgba(255,255,255,0.2)',fontSize:'1.5rem'}}>:</span></div>}
              <div style={{background:isDQ?'rgba(239,68,68,0.15)':bg, border:`1px solid ${isDQ?'rgba(239,68,68,0.6)':col+'50'}`, borderRadius:14, padding:'0.75rem', textAlign:'center'}}>
                <div style={{fontSize:'0.65rem',color:tc,textTransform:'uppercase',marginBottom:'0.2rem',display:'flex',alignItems:'center',justifyContent:'center',gap:'0.3rem'}}>
                  {side==='rot'?'ROT':'BLAU'}{isDQ&&<span style={{color:'#fca5a5',fontWeight:900}}> – DQ!</span>}
                </div>
                <div style={{fontSize:'3.5rem',fontWeight:900,color:isDQ?'rgba(255,255,255,0.2)':col,lineHeight:1,fontVariantNumeric:'tabular-nums'}}>{st[pk]}</div>
                {/* 3-Punkte Verwarnung */}
                <div style={{display:'flex',justifyContent:'center',gap:5,marginTop:'0.4rem'}}>
                  {[0,1,2].map(i=>(
                    <div key={i} style={{width:13,height:13,borderRadius:'50%',background:i<st[wk]?'#f87171':'rgba(255,255,255,0.1)',border:i<st[wk]?'none':'1px solid rgba(255,255,255,0.15)',transition:'background 0.2s'}}/>
                  ))}
                </div>
                <div style={{fontSize:'0.6rem',color:'rgba(255,255,255,0.25)',marginTop:'0.2rem'}}>{st[wk]}/3 Verw.</div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Punkte-Buttons */}
      {[['rot',ROT_C,'#fca5a5'],['blau',BLAU_C,'#93c5fd']].map(([side,col,tc]) => (
        <div key={side} style={{marginBottom:'0.5rem'}}>
          <div style={{fontSize:'0.68rem',color:tc,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:'0.3rem'}}>{side==='rot'?'ROT':'BLAU'} – Punkte</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'0.35rem'}}>
            {[[1,'Technik'],[2,'Kombination / Sprung']].map(([pts,name]) => (
              <button key={pts} onClick={() => score(side,pts)}
                style={{...bs(`rgba(${side==='rot'?'239,68,68':'59,130,246'},${pts===1?0.15:0.35})`,pts===1?tc:'#fff',`${col}50`), flexDirection:'column',gap:'0.15rem',minHeight:56,padding:'0.6rem 0.5rem'}}>
                <span style={{fontSize:'0.875rem'}}>{name}</span>
                <span style={{fontSize:'0.8rem',opacity:0.8}}>+{pts}</span>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Verwarnungen (3 = DQ) */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.4rem',marginBottom:'0.5rem'}}>
        <button onClick={() => warn('rot')}  style={{...bs(ROT_BG,'#fca5a5','rgba(239,68,68,0.3)'),minHeight:44,flexDirection:'column',gap:'0.1rem'}}>
          <span>⚠️ Rot Verwarnung</span>
          <span style={{fontSize:'0.7em',opacity:0.6}}>{st.rote_warnings}/3 → DQ</span>
        </button>
        <button onClick={() => warn('blau')} style={{...bs(BLAU_BG,'#93c5fd','rgba(59,130,246,0.3)'),minHeight:44,flexDirection:'column',gap:'0.1rem'}}>
          <span>⚠️ Blau Verwarnung</span>
          <span style={{fontSize:'0.7em',opacity:0.6}}>{st.blaue_warnings}/3 → DQ</span>
        </button>
      </div>

      {/* Undo (Center only) */}
      {isHaupt && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'0.35rem',marginBottom:'0.6rem'}}>
          {[['↩ Rot Pkt','rot','score'],['↩ Blau Pkt','blau','score'],['↩ Rot ⚠','rot','warn'],['↩ Blau ⚠','blau','warn']].map(([l,c,t]) => (
            <button key={l} onClick={() => t==='warn'?undoWarn(c):undo(c)}
              style={{...bs('rgba(255,255,255,0.04)','rgba(255,255,255,0.4)',BORDER),padding:'0.4rem 0.25rem',fontSize:'0.68rem',minHeight:38,borderRadius:9}}>{l}</button>
          ))}
        </div>
      )}

      {/* Ergebnis */}
      {isHaupt && st.status !== 'finished' && (
        tiebreakerMode ? (
          <div style={{background:'rgba(215,162,58,0.08)',border:'1px solid rgba(215,162,58,0.3)',borderRadius:14,padding:'1rem',marginBottom:'0.6rem'}}>
            <div style={{textAlign:'center',fontWeight:700,color:GOLD,marginBottom:'0.75rem'}}>⚖️ Gleichstand – Entscheidung</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem',marginBottom:'0.5rem'}}>
              <button onClick={() => confirmResult('rot')}  style={{...bs('rgba(239,68,68,0.3)','#fff','rgba(239,68,68,0.5)'),minHeight:56,fontSize:'1rem',flexDirection:'column',gap:'0.2rem'}}><span>🏆 ROT</span><span style={{fontSize:'0.75em',opacity:0.7}}>gewinnt</span></button>
              <button onClick={() => confirmResult('blau')} style={{...bs('rgba(59,130,246,0.3)','#fff','rgba(59,130,246,0.5)'),minHeight:56,fontSize:'1rem',flexDirection:'column',gap:'0.2rem'}}><span>🏆 BLAU</span><span style={{fontSize:'0.75em',opacity:0.7}}>gewinnt</span></button>
            </div>
            <button onClick={() => setTiebreakerMode(false)} style={{...bs('rgba(255,255,255,0.05)','rgba(255,255,255,0.4)',BORDER),width:'100%'}}>Abbrechen</button>
          </div>
        ) : (
          <>
            {/* KO / TKO Buttons */}
            <div style={{fontSize:'0.68rem',color:'rgba(255,255,255,0.35)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:'0.35rem'}}>Sondersieg</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'0.35rem',marginBottom:'0.5rem'}}>
              {[['KO','ko',ROT_C,ROT_BG,'rot'],['TKO','tko',ROT_C,'rgba(239,68,68,0.1)','rot'],['KO','ko',BLAU_C,BLAU_BG,'blau'],['TKO','tko',BLAU_C,'rgba(59,130,246,0.1)','blau']].map(([lbl,method,col,bg,side],i) => (
                <button key={i} onClick={() => confirmResult(side, method)}
                  style={{...bs(bg,col,`${col}50`),minHeight:52,flexDirection:'column',gap:'0.15rem',fontSize:'0.82rem',fontWeight:800}}>
                  <span>{side==='rot'?'🔴':'🔵'}</span>
                  <span>{lbl}</span>
                </button>
              ))}
            </div>
            {/* Punkte-Entscheidung / DQ */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0.4rem',marginBottom:'0.6rem'}}>
              <button onClick={() => confirmResult('rot')}
                style={{...bs(ROT_BG,'#fca5a5','rgba(239,68,68,0.35)'),minHeight:48,flexDirection:'column',gap:'0.15rem',fontSize:'0.82rem'}}>
                <span>🏆</span><span>Rot gewinnt</span>
              </button>
              <button onClick={() => { isTied ? setTiebreakerMode(true) : confirmResult(st.rote_punkte > st.blaue_punkte ? 'rot' : 'blau'); }}
                style={{...bs(isTied?'rgba(215,162,58,0.12)':'rgba(52,211,153,0.12)',isTied?GOLD:GREEN,isTied?'rgba(215,162,58,0.35)':'rgba(52,211,153,0.35)'),minHeight:48,flexDirection:'column',gap:'0.15rem',fontSize:'0.82rem'}}>
                <span>{isTied?'⚖️':'✓'}</span><span>{isTied?'Entscheidung':'Punkte'}</span>
              </button>
              <button onClick={() => confirmResult('blau')}
                style={{...bs(BLAU_BG,'#93c5fd','rgba(59,130,246,0.35)'),minHeight:48,flexDirection:'column',gap:'0.15rem',fontSize:'0.82rem'}}>
                <span>🏆</span><span>Blau gewinnt</span>
              </button>
            </div>
          </>
        )
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  CATEGORY PICKER
// ════════════════════════════════════════════════════════════════════════════
function CategoryPicker({ krToken, onSelect, onClose }) {
  const [step, setStep] = useState('turnier');
  const [turniere, setTurniere] = useState([]);
  const [selTurnier, setSelTurnier] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [selDiv, setSelDiv] = useState(null);
  const [fighters, setFighters] = useState([]);
  const [fightRot, setFightRot] = useState(null);
  const [fightBlau, setFightBlau] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const headers = { Authorization: 'Bearer ' + krToken };

  useEffect(() => {
    setLoading(true);
    fetch('/api/kampfrichter/turniere', { headers })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setTurniere(d.data||[]);
          const aktiv = (d.data||[]).find(t=>t.status==='Aktiv')||d.data?.[0];
          if (aktiv) { setSelTurnier(aktiv); loadDivisions(aktiv.turnier_id); }
        }
      })
      .catch(() => setErr('Fehler'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- laeuft absichtlich nur beim Einhaengen
  }, []);

  async function loadDivisions(tid) {
    setLoading(true);
    try {
      const r = await fetch(`/api/kampfrichter/turnier/${tid}/divisions`, { headers });
      const d = await r.json();
      if (d.success) { setDivisions(d.data||[]); setStep('division'); }
    } catch { setErr('Fehler'); }
    setLoading(false);
  }

  async function loadFighters(divCode) {
    setLoading(true);
    try {
      const r = await fetch(`/api/kampfrichter/turnier/${selTurnier.turnier_id}/division/${encodeURIComponent(divCode)}/fighters`, { headers });
      const d = await r.json();
      if (d.success && d.data?.length>0) { setFighters(d.data); setStep('fighters'); }
      else confirmSelection(divCode,null,null);
    } catch { confirmSelection(divCode,null,null); }
    setLoading(false);
  }

  function confirmSelection(divCode, rot, blau) {
    const div = divisions.find(d => d.division_code === divCode) || selDiv || {};
    const sessionTyp = getSessionTyp(divCode, div.division_type);
    onSelect({ division_code:divCode, division_type:div.division_type||null, division_name:div.division_name||null, turnier_id:selTurnier.turnier_id, turnier_name:selTurnier.name, typ:sessionTyp, rot:rot?`${rot.vorname} ${rot.nachname}`:'', blau:blau?`${blau.vorname} ${blau.nachname}`:'', rot_wettkampfer_id:rot?.wettkampfer_id||null, blau_wettkampfer_id:blau?.wettkampfer_id||null, rot_anmeldung_id:rot?.anmeldung_id||null, blau_anmeldung_id:blau?.anmeldung_id||null, }, sessionTyp, 180);
  }

  const TYPE_COLORS = { kata:['rgba(139,92,246,0.15)','#a78bfa'], bruchtest:['rgba(251,146,60,0.15)','#fb923c'], kickboxen:['rgba(239,68,68,0.15)','#f87171'], kumite:['rgba(215,162,58,0.12)',GOLD] };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', zIndex:200, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#12121f', border:`1px solid ${BORDER}`, borderRadius:'20px 20px 0 0', width:'100%', maxWidth:520, maxHeight:'88vh', overflowY:'auto', padding:'1.25rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
          <div style={{ fontWeight:700, fontSize:'1.1rem' }}>
            {step==='turnier'&&'Turnier wählen'}{step==='division'&&(selTurnier?.name||'Kategorien')}{step==='fighters'&&`${selDiv?.division_code}`}
          </div>
          <button onClick={onClose} style={{...bs('rgba(255,255,255,0.06)','rgba(255,255,255,0.5)',BORDER),padding:'0.4rem 0.75rem',minHeight:36,fontSize:'0.8rem'}}>✕</button>
        </div>
        {err && <div style={{color:'#fca5a5',fontSize:'0.875rem',marginBottom:'0.5rem'}}>{err}</div>}
        {loading && <div style={{color:'rgba(255,255,255,0.35)',padding:'1.5rem',textAlign:'center'}}>Lade…</div>}

        {step==='turnier' && !loading && turniere.map(t => (
          <button key={t.turnier_id} onClick={() => {setSelTurnier(t);loadDivisions(t.turnier_id);}}
            style={{...bs(SURFACE,'#fff',BORDER),width:'100%',justifyContent:'space-between',marginBottom:'0.4rem',padding:'0.9rem 1rem'}}>
            <span>{t.name}</span>
            <span style={{fontSize:'0.75rem',color:t.status==='Aktiv'?GREEN:'rgba(255,255,255,0.3)'}}>{t.status}</span>
          </button>
        ))}

        {step==='division' && !loading && (
          <>
            <button onClick={() => setStep('turnier')} style={{...bs('rgba(255,255,255,0.05)','rgba(255,255,255,0.45)',BORDER),padding:'0.35rem 0.75rem',minHeight:34,fontSize:'0.78rem',marginBottom:'0.75rem'}}>← Turniere</button>
            {divisions.map((div, i) => {
              const typ = getSessionTyp(div.division_code, div.division_type);
              const [typBg,typColor] = TYPE_COLORS[typ]||TYPE_COLORS.kumite;
              // Aufgabe 151: fertige Divisionen kommen vom Server zuletzt — abgeblendet, mit Trennzeile
              const fertig = Number(div.fertig) === 1;
              const ersteFertige = fertig && (i === 0 || Number(divisions[i - 1].fertig) !== 1);
              return (
                <React.Fragment key={div.division_code}>
                {ersteFertige && <div style={{fontSize:'0.72rem',color:'rgba(255,255,255,0.4)',margin:'0.9rem 0 0.4rem',textTransform:'uppercase',letterSpacing:'0.06em'}}>Abgeschlossen</div>}
                <button onClick={() => {setSelDiv(div);setFightRot(null);setFightBlau(null);loadFighters(div.division_code);}}
                  style={{...bs(SURFACE,'#fff',BORDER),width:'100%',justifyContent:'space-between',marginBottom:'0.35rem',padding:'0.7rem 1rem',opacity:fertig?0.5:1}}>
                  <span style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    <span style={{fontSize:'0.7rem',padding:'0.15rem 0.4rem',borderRadius:6,background:typBg,color:typColor,flexShrink:0}}>{div.division_type || (typ==='kata'?'Formen':typ==='bruchtest'?'Bruchtest':typ==='kickboxen'?'Kickboxen':'Kumite')}</span>
                    <span>{div.division_code}</span>
                  </span>
                  <span style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.3)',flexShrink:0}}>{fertig ? '✓ ' : ''}{div.starter_count}</span>
                </button>
                </React.Fragment>
              );
            })}
          </>
        )}

        {step==='fighters' && !loading && (
          <>
            <button onClick={() => setStep('division')} style={{...bs('rgba(255,255,255,0.05)','rgba(255,255,255,0.45)',BORDER),padding:'0.35rem 0.75rem',minHeight:34,fontSize:'0.78rem',marginBottom:'0.75rem'}}>← {selDiv?.division_code}</button>
            {[['rot','ROT-Seite:','#fca5a5',fightRot,setFightRot,'rgba(239,68,68,0.2)','rgba(239,68,68,0.4)'],['blau','BLAU-Seite:','#93c5fd',fightBlau,setFightBlau,'rgba(59,130,246,0.2)','rgba(59,130,246,0.4)']].map(([side,label,tc,sel,setSel,bg,border]) => (
              <div key={side}>
                <div style={{fontSize:'0.78rem',color:tc,margin:'0.75rem 0 0.4rem'}}>{label}</div>
                {fighters.filter(f=>f.anmeldung_id!==((side==='rot'?fightBlau:fightRot)?.anmeldung_id)).map(f=>(
                  <button key={f.anmeldung_id} onClick={()=>setSel(f)}
                    style={{...bs(sel?.anmeldung_id===f.anmeldung_id?bg:SURFACE,'#fff',sel?.anmeldung_id===f.anmeldung_id?border:BORDER),width:'100%',justifyContent:'flex-start',marginBottom:'0.3rem',padding:'0.6rem 1rem',gap:'0.5rem'}}>
                    <span style={{fontWeight:600}}>{f.vorname} {f.nachname}</span>
                    {f.verein_name&&<span style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.35)'}}>{f.verein_name}</span>}
                  </button>
                ))}
              </div>
            ))}
            <div style={{display:'flex',flexDirection:'column',gap:'0.4rem',marginTop:'0.75rem'}}>
              <button onClick={() => confirmSelection(selDiv.division_code,fightRot,fightBlau)}
                style={{...bs(GOLD,'#000',GOLD),width:'100%',fontSize:'1rem',padding:'0.9rem'}}>✓ Kampf starten</button>
              <button onClick={() => confirmSelection(selDiv.division_code,null,null)}
                style={{...bs('rgba(255,255,255,0.05)','rgba(255,255,255,0.4)',BORDER),width:'100%'}}>Ohne Namen starten</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
